import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RepoSelector } from "../components/RepoSelector";

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function SectionHeader({ icon, title }: { icon: string; title: string }) {
  return (
    <div className="flex items-center gap-2 border-l-4 border-primary pl-3 mb-5">
      <span className="material-symbols-outlined text-primary">{icon}</span>
      <h3 className="text-xl font-bold">{title}</h3>
    </div>
  );
}

function PlatformCard({
  icon,
  name,
  description,
  checked,
  onChange,
}: {
  icon: string;
  name: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label
      className={`cursor-pointer relative flex items-center gap-4 p-4 rounded-lg border transition-all ${
        checked
          ? "border-primary bg-primary/5 hover:bg-primary/10"
          : "border-border bg-card hover:border-muted-foreground/50 hover:bg-muted/50"
      }`}
    >
      <div
        className={`size-10 rounded-full flex items-center justify-center border ${
          checked
            ? "bg-card text-primary border-border shadow-sm"
            : "bg-muted text-muted-foreground border-border"
        }`}
      >
        <span className="material-symbols-outlined">{icon}</span>
      </div>
      <div className="flex-1">
        <p className={`font-medium ${checked ? "font-bold" : ""}`}>{name}</p>
        <p className="text-muted-foreground text-sm">{description}</p>
      </div>
      <div className={checked ? "text-primary" : "text-muted-foreground/30"}>
        <span className="material-symbols-outlined">
          {checked ? "check_circle" : "circle"}
        </span>
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />
    </label>
  );
}

export function NewProjectPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [buildWindows, setBuildWindows] = useState(true);
  const [buildLinux, setBuildLinux] = useState(true);
  const [buildMacos, setBuildMacos] = useState(true);
  // GitHub integration
  const [githubRepoFullName, setGithubRepoFullName] = useState<string | null>(null);
  const [githubBranch, setGithubBranch] = useState("main");
  const [githubInstallationId, setGithubInstallationId] = useState<number | null>(null);
  const [githubRepoId, setGithubRepoId] = useState<number | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch("/api/projects", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name,
          slug: generateSlug(name),
          buildWindows,
          buildLinux,
          buildMacos,
          githubRepoFullName,
          githubBranch,
          githubInstallationId,
          githubRepoId,
        }),
      });

      if (!response.ok) {
        const { error: errorMsg } = await response.json();
        throw new Error(errorMsg || "Failed to create project");
      }

      const { data: project } = await response.json();
      navigate(`/projects/${project.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto w-full px-6 py-8">
      {/* Breadcrumb and Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div className="flex flex-col gap-2">
          {/* Breadcrumbs */}
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <Link to="/projects" className="hover:text-primary transition-colors flex items-center gap-1">
              <span className="material-symbols-outlined text-[18px]">folder_open</span>
              Projects
            </Link>
            <span className="material-symbols-outlined text-[16px]">chevron_right</span>
            <span className="font-medium text-foreground">New Project</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight">Create New Project</h1>
          <p className="text-muted-foreground">
            Set up a new Godot game build pipeline
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 text-destructive rounded-lg flex items-center gap-2">
          <span className="material-symbols-outlined">error</span>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Project Details */}
        <section>
          <SectionHeader icon="edit" title="Project Details" />
          <div className="p-6 rounded-xl bg-card border border-border shadow-sm space-y-6">
            <div className="flex flex-col">
              <Label htmlFor="name" className="text-base font-medium pb-2">
                Project Name
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My Awesome Game"
                required
                className="h-12"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Choose a memorable name for your game project
              </p>
            </div>

            <div className="flex flex-col">
              <div className="flex justify-between items-center pb-2">
                <Label className="text-base font-medium">GitHub Repository</Label>
                {githubRepoFullName && (
                  <span className="text-xs text-emerald-600 font-bold uppercase tracking-wider flex items-center gap-1">
                    <span className="material-symbols-outlined text-[14px]">check_circle</span>
                    Connected
                  </span>
                )}
              </div>
              <RepoSelector
                value={githubRepoFullName || undefined}
                selectedBranch={githubBranch}
                onChange={(repo) => {
                  if (repo) {
                    setGithubRepoFullName(repo.repoFullName);
                    setGithubInstallationId(repo.installationId);
                    setGithubRepoId(repo.repoId);
                    // Auto-set name from repo name if empty
                    if (!name) {
                      setName(repo.repoName.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()));
                    }
                  } else {
                    setGithubRepoFullName(null);
                    setGithubInstallationId(null);
                    setGithubRepoId(null);
                  }
                }}
                onBranchChange={setGithubBranch}
              />
              <p className="text-xs text-muted-foreground mt-2">
                Optional: Connect a GitHub repository to enable automatic builds on push or tag
              </p>
            </div>
          </div>
        </section>

        {/* Platform Targets */}
        <section>
          <SectionHeader icon="devices" title="Platform Targets" />
          <div className="flex flex-col gap-3 p-6 rounded-xl bg-card border border-border shadow-sm">
            <p className="text-muted-foreground text-sm mb-2">
              Select which platforms to build your game for
            </p>
            <PlatformCard
              icon="window"
              name="Windows"
              description="x86_64, Windows 10+"
              checked={buildWindows}
              onChange={setBuildWindows}
            />
            <PlatformCard
              icon="terminal"
              name="Linux"
              description="x86_64, Ubuntu 20.04+"
              checked={buildLinux}
              onChange={setBuildLinux}
            />
            <PlatformCard
              icon="desktop_mac"
              name="macOS"
              description="Universal (Apple Silicon + Intel)"
              checked={buildMacos}
              onChange={setBuildMacos}
            />
          </div>
        </section>

        {/* Footer Actions */}
        <div className="flex flex-col sm:flex-row items-center justify-end gap-4 pt-8 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/projects")}
            className="w-full sm:w-auto h-12 px-6"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading || !name.trim()}
            className="w-full sm:w-auto h-12 px-6 shadow-lg shadow-primary/20"
          >
            <span className="material-symbols-outlined text-[20px]">add</span>
            {loading ? "Creating..." : "Create Project"}
          </Button>
        </div>
      </form>
    </div>
  );
}
